import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { MapPin, Search, Home, Briefcase, Navigation } from 'lucide-react'
import { logError, logWarning, log } from '@/utils/logger'

// Google Maps types - window.google is available globally when Google Maps API is loaded
declare global {
  interface Window {
    google?: {
      maps?: {
        Map: new (mapDiv: HTMLElement | null, opts?: any) => any
        Marker: new (opts?: any) => any
        Geocoder: new () => any
        places?: {
          Autocomplete: new (inputField: HTMLInputElement, opts?: any) => any
        }
        ControlPosition: any
        Animation: any
        event?: {
          removeListener(listener: any): void
          clearInstanceListeners?: (instance: any) => void
        }
        marker?: {
          AdvancedMarkerElement?: unknown
          PinElement?: unknown
        }
      }
    }
    initMap: () => void
  }
}

interface MapPanelProps {
  selectedLatLng: { lat: number; lng: number } | null
  selectedAddressText: string
  onLocationSelected: (lat: number, lng: number, address: string) => void
  isRTL: boolean
}

/**
 * Map Panel Component for selecting address location
 * Uses Google Maps JavaScript API for better interactivity
 */
export const MapPanel: React.FC<MapPanelProps> = ({
  selectedLatLng,
  selectedAddressText,
  onLocationSelected,
  isRTL,
}) => {
  const { t } = useTranslation()
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: 24.7136, lng: 46.6753 }) // Riyadh default
  const mapCenterRef = useRef(mapCenter)
  const [isMapReady, setIsMapReady] = useState(false)
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false)
  const mapRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const autocompleteRef = useRef<any>(null)
  const mapInstanceRef = useRef<any>(null)
  const markerRef = useRef<any>(null)
  const geocoderRef = useRef<any>(null)
  const isMountedRef = useRef(true)
  const listenersRef = useRef<any[]>([])
  const onLocationSelectedRef = useRef(onLocationSelected)
  const [searchValue, setSearchValue] = useState('')
  const isUserTypingRef = useRef(false)
  
  // Keep callback ref updated
  useEffect(() => {
    onLocationSelectedRef.current = onLocationSelected
  }, [onLocationSelected])

  // Separate cleanup effect for unmount only
  useEffect(() => {
    return () => {
      isMountedRef.current = false
      
      // Cleanup on unmount - use requestAnimationFrame to ensure React has finished
      requestAnimationFrame(() => {
        try {
          // Remove all listeners
          if (listenersRef.current && listenersRef.current.length > 0) {
            listenersRef.current.forEach(listener => {
              try {
                if (listener && listener.remove) {
                  listener.remove()
                } else if (listener && window.google?.maps?.event) {
                  window.google.maps.event.removeListener(listener)
                }
              } catch (err) {
                // Ignore errors
              }
            })
            listenersRef.current = []
          }

          // Clean up marker
          if (markerRef.current) {
            try {
              // Use mapInstanceRef to check if marker is on map
              if (mapInstanceRef.current) {
                markerRef.current.setMap(null)
              }
            } catch (err) {
              // Ignore errors
            }
            markerRef.current = null
          }

          // Clear map instance reference
          mapInstanceRef.current = null
          geocoderRef.current = null
          
          // Cleanup autocomplete
          if (autocompleteRef.current) {
            try {
              const event = window.google?.maps?.event as any
              if (event?.clearInstanceListeners) {
                event.clearInstanceListeners(autocompleteRef.current)
              }
            } catch (err) {
              // Ignore errors
            }
            autocompleteRef.current = null
          }
        } catch (error) {
          // Silently ignore cleanup errors
        }
      })
    }
  }, [])

  // Get Google Maps API Key from environment
  // Fallback to the key used in Flutter project if not set in .env
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || 'AIzaSyCjmLLYFQ_iFRj-g_b4YuJrHsG_wN8ZRVE'

  // Check if Google Maps is loaded
  useEffect(() => {
    const checkGoogleMaps = (): boolean => {
      return !!(window.google && window.google.maps && window.google.maps.Map)
    }

    if (checkGoogleMaps()) {
      setIsGoogleMapsLoaded(true)
      return
    }

    // If not loaded, load it dynamically
    if (!document.querySelector('script[src*="maps.googleapis.com"]')) {
      if (!googleMapsApiKey) {
        setIsMapReady(true)
        return
      }

      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places,geometry,marker&language=ar&callback=initMap&loading=async`
      script.async = true
      script.defer = true
      script.onerror = () => {
        setIsMapReady(true) // Show fallback
      }
      
      window.initMap = () => {
        setIsGoogleMapsLoaded(true)
      }
      
      document.head.appendChild(script)
    } else {
      // Script exists, wait for it to load
      const interval = setInterval(() => {
        if (checkGoogleMaps()) {
          setIsGoogleMapsLoaded(true)
          clearInterval(interval)
        }
      }, 100)

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(interval)
        if (!checkGoogleMaps()) {
          setIsMapReady(true)
        }
      }, 10000)

      return () => clearInterval(interval)
    }
  }, [googleMapsApiKey])

  useEffect(() => {
    mapCenterRef.current = mapCenter
  }, [mapCenter])

  // Initialize map when Google Maps is loaded
  useEffect(() => {
    if (!isGoogleMapsLoaded || !mapRef.current || mapInstanceRef.current) {
      return
    }

    isMountedRef.current = true
    let map: google.maps.Map | null = null
    let marker: google.maps.Marker | null = null
    let mapClickListener: any = null

    try {
      // Store reference to map container before Google Maps modifies it
      const mapContainer = mapRef.current
      if (!mapContainer || !mapContainer.parentNode) {
        return
      }

      // Initialize map with optimized settings for better performance
      const initialCenter = mapCenterRef.current
      map = new window.google.maps.Map(mapContainer, {
        center: initialCenter,
        zoom: 15,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: false,
        gestureHandling: 'greedy', // Allow one-finger panning
        clickableIcons: false, // Disable POI clicks for cleaner interaction
        disableDefaultUI: false,
        keyboardShortcuts: true,
      } as any)

      mapInstanceRef.current = map

      // Initialize geocoder
      geocoderRef.current = new window.google.maps.Geocoder()

      // Don't add marker initially - only add when user clicks on map
      markerRef.current = null

      // Handle map click - immediate response like Google Maps
      mapClickListener = map.addListener('click', (e: any) => {
        if (!isMountedRef.current || !map) return
        
        if (e.latLng) {
          const lat = e.latLng.lat()
          const lng = e.latLng.lng()
          
          // Immediately update map center for smooth experience
          map.setCenter({ lat, lng })
          
          // Create or update marker at clicked position with smooth animation
          if (!markerRef.current) {
            marker = new window.google.maps.Marker({
              position: { lat, lng },
              map: map,
              draggable: false,
              animation: window.google.maps.Animation.DROP,
              optimized: true,
            } as any)
            markerRef.current = marker
          } else {
            marker = markerRef.current
            // Use smooth animation when moving marker
            ;(marker as any).setAnimation(window.google.maps.Animation.DROP)
            marker.setPosition({ lat, lng })
          }

          // Don't update search value with coordinates - wait for geocoding
          // Only call callback with coordinates for internal use (without updating search field)
          // Reverse geocode to get address first
          if (geocoderRef.current) {
            geocoderRef.current.geocode(
              { location: { lat, lng } },
              (results, status) => {
                if (!isMountedRef.current) return
                
                if (status === 'OK' && results && results[0]) {
                  const address = results[0].formatted_address
                  // Update with full address once geocoding completes
                  onLocationSelectedRef.current(lat, lng, address)
                  // Update search value with the actual address
                  isUserTypingRef.current = false
                  setSearchValue(address)
                } else {
                  // If geocoding fails, use coordinates as fallback but don't show in search
                  onLocationSelectedRef.current(lat, lng, `${lat}, ${lng}`)
                }
              }
            )
          } else {
            // Fallback if geocoder is not available
            onLocationSelectedRef.current(lat, lng, `${lat}, ${lng}`)
          }
        }
      })

      // Store listeners for cleanup
      listenersRef.current = [mapClickListener]

      setIsMapReady(true)
      
      // Initialize Autocomplete for search
      if (searchInputRef.current && window.google?.maps?.places?.Autocomplete) {
        const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
          fields: ['geometry', 'formatted_address', 'name'],
          // No country restrictions - search worldwide
        })
        
        autocompleteRef.current = autocomplete
        
        // Handle place selection - smooth transition like Google Maps
        autocomplete.addListener('place_changed', () => {
          if (!isMountedRef.current || !autocomplete || !map) return
          
          const place = autocomplete.getPlace()
          
          // Check if place has geometry
          if (!place.geometry || !place.geometry.location) {
            logWarning('Selected place has no geometry')
            return
          }
          
          // Handle both LatLng object and LatLngLiteral formats
          let lat: number
          let lng: number
          
          const location = place.geometry.location
          if (typeof location.lat === 'function') {
            // It's a LatLng object
            lat = location.lat()
            lng = location.lng()
          } else {
            // It's a LatLngLiteral
            lat = location.lat
            lng = location.lng
          }
          
          const address = place.formatted_address || place.name || ''
          
          // Update map center first
          const newCenter = { lat, lng }
          setMapCenter(newCenter)
          
          // Smoothly pan and zoom to the selected place
          map.setCenter(newCenter)
          map.setZoom(17)
          
          // Also use panTo for smooth animation
          ;(map as any).panTo(newCenter)
          
          // Create or update marker at selected place with animation
          if (!markerRef.current) {
            const newMarker = new window.google.maps.Marker({
              position: newCenter,
              map: map,
              draggable: false,
              animation: window.google.maps.Animation.DROP,
              optimized: true,
            } as any)
            markerRef.current = newMarker
          } else {
            ;(markerRef.current as any).setAnimation(window.google.maps.Animation.DROP)
            markerRef.current.setPosition(newCenter)
          }
          
          // Update search input value
          isUserTypingRef.current = false
          setSearchValue(address)
          
          // Immediately call location selected callback
          onLocationSelectedRef.current(lat, lng, address)
        })
      }
    } catch (error) {
      setIsMapReady(true) // Show fallback
    }

    // Cleanup function - only cleanup when dependencies change, not on unmount
    return () => {
      // Only cleanup listeners and marker when re-initializing
      // Full cleanup is handled by the unmount effect
      try {
        // Remove listeners from current refs
        if (listenersRef.current && listenersRef.current.length > 0) {
          listenersRef.current.forEach(listener => {
            try {
              if (listener && listener.remove) {
                listener.remove()
              } else if (listener && window.google?.maps?.event) {
                window.google.maps.event.removeListener(listener)
              }
            } catch (err) {
              // Ignore errors
            }
          })
          listenersRef.current = []
        }

        // Also cleanup the local listeners
        if (mapClickListener) {
          try {
            if (mapClickListener.remove) {
              mapClickListener.remove()
            } else if (window.google?.maps?.event) {
              window.google.maps.event.removeListener(mapClickListener)
            }
          } catch (err) {
            // Ignore errors
          }
        }

        // Remove marker from map but keep refs for unmount cleanup
        if (marker) {
          try {
            // Use mapInstanceRef to check if marker is on map
            if (mapInstanceRef.current) {
              marker.setMap(null)
            }
          } catch (err) {
            // Ignore errors
          }
        }

        // Don't null the refs here - unmount effect will handle that
        // Just clear the map instance reference to allow re-initialization
        if (mapInstanceRef.current === map) {
          mapInstanceRef.current = null
        }
        if (markerRef.current === marker) {
          markerRef.current = null
        }
        
        // Cleanup autocomplete when re-initializing
        if (autocompleteRef.current) {
          try {
            const event = window.google?.maps?.event as any
            if (event?.clearInstanceListeners) {
              event.clearInstanceListeners(autocompleteRef.current)
            }
          } catch (err) {
            // Ignore errors
          }
          autocompleteRef.current = null
        }
      } catch (error) {
        // Silently ignore cleanup errors
      }
    }
  }, [isGoogleMapsLoaded])

  // Get user's current location if available
  useEffect(() => {
    if (!isGoogleMapsLoaded) return

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newCenter = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }
          setMapCenter(newCenter)
          if (mapInstanceRef.current) {
            mapInstanceRef.current.setCenter(newCenter)
          }
          if (markerRef.current) {
            markerRef.current.setPosition(newCenter)
          }
        },
        () => {
          // Use default location if geolocation fails
        }
      )
    }
  }, [isGoogleMapsLoaded])

  // Update map center when location is selected
  useEffect(() => {
    if (selectedLatLng && mapInstanceRef.current) {
      setMapCenter(selectedLatLng)
      mapInstanceRef.current.setCenter(selectedLatLng)
      
      // Create or update marker if location is selected externally
      if (markerRef.current) {
        markerRef.current.setPosition(selectedLatLng)
      } else if (isGoogleMapsLoaded && window.google?.maps?.Marker) {
        const marker = new window.google.maps.Marker({
          position: selectedLatLng,
          map: mapInstanceRef.current,
          draggable: false,
          animation: window.google.maps.Animation.DROP,
        })
        markerRef.current = marker
      }
    }
  }, [selectedLatLng, isGoogleMapsLoaded])
  
  // Check if text is coordinates (numeric format like "lat, lng")
  const isCoordinates = (text: string): boolean => {
    if (!text) return false
    // Check if text matches pattern like "31.218627101029718, 29.94917086832013"
    const coordPattern = /^-?\d+\.?\d*\s*,\s*-?\d+\.?\d*$/
    return coordPattern.test(text.trim())
  }

  // Update search value when selectedAddressText changes (but not when user is typing)
  useEffect(() => {
    // Don't update if user is currently typing or editing
    if (isUserTypingRef.current) {
      return
    }
    
    if (selectedAddressText) {
      const placeholder = t('cs.select_location_on_map') || 'اختر الموقع على الخريطة'
      // Don't update search value if selectedAddressText is coordinates
      if (isCoordinates(selectedAddressText)) {
        return
      }
      // Only update if the search input is empty or matches the placeholder or selected address
      // This prevents overwriting user input while typing
      if (!searchValue || searchValue === placeholder || searchValue === selectedAddressText) {
        isUserTypingRef.current = false
        setSearchValue(selectedAddressText)
      }
    }
  }, [selectedAddressText, t, searchValue])

  const shouldShowFallbackMap =
    !googleMapsApiKey || (!isGoogleMapsLoaded && isMapReady)

  // Extract location code from address text
  const extractLocationCode = (address: string) => {
    if (!address) return ''
    const match = address.match(/([A-Z0-9]+\+[A-Z0-9]+)/)
    return match ? match[1] : ''
  }

  const locationCode = extractLocationCode(selectedAddressText)
  const displayText = selectedAddressText || t('cs.select_location_on_map') || 'اختر الموقع على الخريطة'

  // Handle current location button click
  const handleCurrentLocation = () => {
    log('Current location button clicked', { 
      mapInstance: !!mapInstanceRef.current, 
      geolocation: !!navigator.geolocation 
    })
    
    if (!mapInstanceRef.current) {
      logWarning('Map instance not available')
      return
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          log('Got position:', position.coords)
          const currentLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }
          
          log('Setting map center to:', currentLocation)
          
          // Center map on current location
          if (mapInstanceRef.current) {
            mapInstanceRef.current.setCenter(currentLocation)
            mapInstanceRef.current.setZoom(17)
          }
          
          // Update marker
          if (markerRef.current) {
            markerRef.current.setPosition(currentLocation)
            if (window.google?.maps?.Animation) {
              try {
                (markerRef.current as any).setAnimation(window.google.maps.Animation.DROP)
              } catch (e) {
                // Animation not supported
              }
            }
          } else if (isGoogleMapsLoaded && window.google?.maps?.Marker && mapInstanceRef.current) {
            const markerOptions: any = {
              position: currentLocation,
              map: mapInstanceRef.current,
              draggable: false,
            }
            if (window.google.maps.Animation) {
              markerOptions.animation = window.google.maps.Animation.DROP
            }
            const marker = new window.google.maps.Marker(markerOptions)
            markerRef.current = marker
          }
          
          // Don't update search value with coordinates - wait for geocoding
          // Geocode to get address first
          if (geocoderRef.current) {
            geocoderRef.current.geocode(
              { location: currentLocation },
              (results, status) => {
                log('Geocode status:', status, results)
                if (status === 'OK' && results && results[0]) {
                  const address = results[0].formatted_address
                  log('Got address:', address)
                  onLocationSelectedRef.current(currentLocation.lat, currentLocation.lng, address)
                  isUserTypingRef.current = false
                  setSearchValue(address)
                } else {
                  logWarning('Geocoding failed, using coordinates')
                  // Call with coordinates but don't show in search field
                  onLocationSelectedRef.current(currentLocation.lat, currentLocation.lng, `${currentLocation.lat}, ${currentLocation.lng}`)
                }
              }
            )
          } else {
            logWarning('Geocoder not available')
            // Fallback: call with coordinates but don't show in search field
            onLocationSelectedRef.current(currentLocation.lat, currentLocation.lng, `${currentLocation.lat}, ${currentLocation.lng}`)
          }
        },
        (error) => {
          // Handle geolocation error
          logError('Error getting current location:', error)
          let errorMessage = 'حدث خطأ في الحصول على الموقع'
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'تم رفض طلب الوصول إلى الموقع'
              break
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'معلومات الموقع غير متاحة'
              break
            case error.TIMEOUT:
              errorMessage = 'انتهت مهلة طلب الموقع'
              break
          }
          alert(errorMessage)
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      )
    } else {
      logError('Geolocation is not supported by this browser')
      alert('المتصفح لا يدعم تحديد الموقع')
    }
  }

  return (
    <div className="w-full h-full flex flex-col px-4">
      <div
        className="w-full h-full rounded-xl overflow-hidden border border-gray-300 relative"
        style={{ minHeight: '300px' }}
      >
        {/* Search Bar - Inside map container */}
        <div className="absolute top-4 left-4 right-4 z-20">
          <div className={`bg-white  backdrop-blur-sm rounded-xl shadow-lg border border-gray-200 px-4 py-3 flex items-center gap-0 flex-row-reverse`}>
            <input
              ref={searchInputRef}
              type="text"
              value={searchValue}
              onChange={(e) => {
                isUserTypingRef.current = true
                setSearchValue(e.target.value)
              }}
              onBlur={() => {
                // Reset typing flag after a short delay to allow for programmatic updates
                setTimeout(() => {
                  isUserTypingRef.current = false
                }, 100)
              }}
              onKeyDown={(e) => {
                // Mark as typing when user presses any key
                isUserTypingRef.current = true
              }}
              placeholder={displayText}
              className={`no-focus flex-1 min-w-0 bg-transparent border-none outline-none text-sm font-medium font-['IBM_Plex_Sans_Arabic'] ${isRTL ? 'text-right' : 'text-left'} placeholder:text-gray-400 focus:placeholder:text-gray-300 transition-colors`}
              style={{ 
                color: searchValue ? '#111827' : '#6B7280'
              }}
            />
                        <Search className="w- h-5 text-gray-500 flex-shrink-0" />

          </div>
        </div>

        {/* Map */}
        <div ref={mapRef} className="w-full h-full" />

        {/* Current Location Button */}
        {!shouldShowFallbackMap && isMapReady && (
          <button
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleCurrentLocation()
            }}
            className="absolute right-2 bottom-20 z-30
                       w-11 h-11 rounded-full bg-white shadow-lg
                       flex items-center justify-center
                       hover:bg-gray-50 active:scale-95
                       transition-all duration-200
                       border border-gray-200
                       pointer-events-auto cursor-pointer"
            aria-label={t('cs.current_location') || 'الموقع الحالي'}
            type="button"
          >
            <Navigation className="w-6 h-6 text-gray-700" />
          </button>
        )}

        {shouldShowFallbackMap ? (
          <div className="absolute inset-0">
            {isMapReady ? (
              <iframe
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps/embed/v1/place?key=${googleMapsApiKey}&q=${mapCenter.lat},${mapCenter.lng}&zoom=15`}
                title="Map"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--brand-primary)]"></div>
              </div>
            )}
          </div>
        ) : (
          !isMapReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
              <div className="flex flex-col items-center gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--brand-primary)]"></div>
                <p className="text-sm text-gray-600 font-['IBM_Plex_Sans_Arabic']">
                  {t('cs.loading_map') || 'جاري تحميل الخريطة...'}
                </p>
              </div>
            </div>
          )
        )}

       
      </div>
    </div>
  )
}

/**
 * Address Type Selection Component
 */
export const AddressTypeSelector: React.FC<{
  selectedType: string
  onTypeChange: (type: string) => void
  isRTL: boolean
}> = ({ selectedType, onTypeChange, isRTL }) => {
  const { t } = useTranslation()

  const addressTypes = [
    { value: 'home', label: t('cs.address_type_home') || 'منزل', icon: Home },
    { value: 'work', label: t('cs.address_type_work') || 'عمل', icon: Briefcase },
    { value: 'other', label: t('cs.address_type_other') || 'أخرى', icon: MapPin },
  ]

  return (
    <div className="w-full">
      <label className="text-sm font-semibold text-gray-700 mb-3 block font-['IBM_Plex_Sans_Arabic']">
        {t('cs.address_type') || 'نوع العنوان'}
      </label>
      <div className="flex flex-row gap-3">
        {addressTypes.map((type) => {
          const Icon = type.icon
          const isSelected = selectedType === type.value
          return (
            <button
              key={type.value}
              onClick={() => onTypeChange(type.value)}
              className={`
                flex-1 flex flex-row items-center justify-center gap-2
                py-3 px-2 rounded-xl
                transition-all
                font-medium text-sm font-['IBM_Plex_Sans_Arabic']
                ${isSelected 
                  ? 'border-2' 
                  : 'border border-gray-300'
                }
              `}
              style={{
                borderColor: isSelected ? 'var(--brand-primary)' : '#D1D5DB',
                backgroundColor: isSelected ? '#ffffff' : '#FFFFFF',
                color: isSelected ? 'var(--brand-primary)' : '#374151',
              }}
            >
              <Icon 
                className="w-6 h-6" 
                style={{
                  color: isSelected ? 'var(--brand-primary)' : '#6B7280',
                }}
              />
              <span>{type.label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Apartment Number Input Component
 */
export const ApartmentInput: React.FC<{
  value: string
  onChange: (value: string) => void
  isRTL: boolean
}> = ({ value, onChange, isRTL }) => {
  const { t } = useTranslation()

  return (
    <div className="w-full">
      <label className="text-sm font-semibold text-gray-700 mb-2 block font-['IBM_Plex_Sans_Arabic']">
        {t('cs.apartment_villa') || 'الشقة/الفيلا'}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('cs.fifth_floor_apartment_4') || 'مثال: الطابق الخامس، شقة 4'}
        className={`
          w-full h-12 px-4 rounded-xl border border-gray-300 bg-gray-50
          text-sm font-medium font-['IBM_Plex_Sans_Arabic']
          focus:outline-none focus:border-[var(--brand-primary)]
          ${isRTL ? 'text-right' : 'text-left'}
        `}
      />
    </div>
  )
}

